'use client';

import { useCallback, useEffect, useRef } from 'react';
import { useInViewport } from 'ahooks';
import { useAtomValue } from 'jotai';
import { track } from '@/utils/track';
import { setWithdrawBannerSource } from '@/utils/banner-source';
import { Env } from '@/libs/env';
import { isLoginAtom } from '@/store';
import { showLoginModal } from '@/features/auth/components/login-form';
import TitleBar from '../components/title-bar';
import ImageLoad from '../components/image-load';
import { bottomBannerDataAtom } from '../store/home';
import { AudioId, useAudioHelper } from '@/utils/audio-hepler';
import { isJumpAction, jumpByUrl, parseJumpAction } from '@/utils/jump-url';
import { useRouter } from '@/i18n/navigation';
import type { CardBannerItem } from '../services/home';
import { JUMP_ACTION } from '@/const/jump-action';

interface BannerItemProps {
  index: number;
  data: CardBannerItem,
  from?: string;
  onClick: (item: CardBannerItem, index: number) => void;
}

function BannerItem({ from, index, data, onClick }: BannerItemProps) {
  // 使用条件渲染可以完全控制何时加载图片
  const containerRef = useRef<HTMLDivElement>(null);
  const [inViewport] = useInViewport(containerRef, {
    rootMargin: '0px 0px', // 提前100px开始加载，提升用户体验
  });

  useEffect(() => {
    if (inViewport) {
      const { link } = data || {};
      track('banner_exposure', {
        current_page: from,
        current_module: data?.module_name,
        banner_number: index + 1,
        to_url: link,
      });
    }
  }, [data, from, inViewport, index]);

  return <div className="relative aspect-[3/1] rounded-[10px] mt-[10px] click-effect-block" ref={containerRef}>
    <ImageLoad
      src={data?.img}
      alt='banner-img'
      fill
      className='object-cover'
      onClick={() => onClick?.(data as CardBannerItem, index)}
    />
  </div>;
}

interface FooterContentProps {
  from?: string;
}

export default function FooterContent(props: FooterContentProps) {
  const { from = 'home' } = props;
  const bottomBanners = useAtomValue(bottomBannerDataAtom);
  const isLogin = useAtomValue(isLoginAtom);
  // 视口检测：用于懒加载图片资源
  // 注意：Next.js Image 的 loading="lazy" 对静态导入的图片和 fill 属性可能不生效
  // 使用条件渲染可以完全控制何时加载图片
  const containerRef = useRef<HTMLDivElement>(null);
  const [inViewport] = useInViewport(containerRef, {
    rootMargin: '100px 0px', // 提前100px开始加载，提升用户体验
  });
  const { playAudio } = useAudioHelper({ id: AudioId.ButtonClick });
  const router = useRouter();

  const handleBanner = useCallback((item: CardBannerItem, index: number) => {
    playAudio();
    const { link, has_activity_common_detail } = item || {};
    track('banner_click', {
      current_page: from,
      current_module: item?.module_name,
      banner_number: index + 1,
      to_url: link,
    });
    // 处理跳转
    if (link) {
      if (isJumpAction(link)) {
        const { action } = parseJumpAction(link) || {};
        if (!isLogin) {
          const walletActions = [
            JUMP_ACTION.OPEN_DEPOSIT_CASH,
            JUMP_ACTION.OPEN_DEPOSIT_CRYPTO,
            JUMP_ACTION.OPEN_WITHDRAW_CASH,
            JUMP_ACTION.OPEN_WITHDRAW_CRYPTO,
            JUMP_ACTION.OPEN_AFFILIATE_SHARE,
            JUMP_ACTION.OPEN_WALLET_MODAL
          ];
          if (action && walletActions.includes(action)) {
            showLoginModal();
            return;
          }
        }
        if (isLogin && (action === JUMP_ACTION.OPEN_WITHDRAW_CASH || action === JUMP_ACTION.OPEN_WITHDRAW_CRYPTO)) {
          setWithdrawBannerSource('home_page_bottom');
        }
      }
      jumpByUrl(link, router, item?.module_name);
    } else if (has_activity_common_detail) {
      router.push(`/banner-detail?id=${item.uuid}&type=1`);
    }
  }, [from, isLogin, playAudio, router]);

  if (!bottomBanners || bottomBanners.length === 0) {
    return null;
  }

  return (
    <>
      <TitleBar leftTitle={Env.NEXT_PUBLIC_APP_SLOGAN} rightTitleClassName="!pl-[12px]"></TitleBar>
      <div ref={containerRef} className="px-[12px] mt-[15px]">
        {
          (bottomBanners || []).map((item, index) => {
            return <div className="relative aspect-[3/1] rounded-[10px] mt-[10px] click-effect-block" key={`banner-${index}`}>
              {inViewport && <BannerItem key={item.uuid} from={from} data={item} index={index} onClick={handleBanner} />}
            </div>;
          })
        }
      </div>
    </>
  );
}
